import React, { useState, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import styles from '../../../components/UI/SelectProductCatalogueModal.module.css';
import { useAmazonSync } from '../../../context/AmazonSyncContext';

const PRODUCTS_PER_PAGE = 12; // 3 rows of 4 shown at once; more via pagination
const PER_ROW = 4;
// Height a full page of products occupies (rows × card height + gaps), so the loading
// placeholder reserves the same space instead of shrinking the panel. Card = 92px, gap = 16px.
const ROWS_PER_PAGE = Math.ceil(PRODUCTS_PER_PAGE / PER_ROW);
const GRID_MIN_HEIGHT = ROWS_PER_PAGE * 92 + (ROWS_PER_PAGE - 1) * 16;

// Embedded (inline) product catalogue for the existing-mode ASIN lookup step.
// Unlike SelectProductCatalogueModal (a floating popup), this renders in the normal
// document flow, is filtered by the ASIN lookup input (searchTerm), shows 4 cards per
// row, and has no close/action button — clicking a card populates the ASIN input above,
// and the step's "Fetch" button loads it. Reuses the modal's CSS for card styling.
export const InlineProductCatalogue = ({
  products = [],
  selectedAsins = [],
  onAsinSelect,
  loading = false,
  searchTerm = '',
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [showAsin, setShowAsin] = useState(false);
  const { status: syncStatus, error: syncError } = useAmazonSync();

  const query = (searchTerm || '').trim().toLowerCase();
  // When the search box holds a selected product's ASIN (the user picked a card rather
  // than typing a search), keep the full grid instead of collapsing to that one product.
  const isSelectionQuery = query && selectedAsins.some(a => (a || '').toLowerCase() === query);
  const filteredProducts = (query && !isSelectionQuery)
    ? products.filter(p =>
        [p.title, p.sku, p.asin].some(field => field && field.toLowerCase().includes(query))
      )
    : products;

  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const startIdx = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const pageProducts = filteredProducts.slice(startIdx, startIdx + PRODUCTS_PER_PAGE);

  // Reset to the first page whenever the search term changes.
  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  // Catalogue not yet available — show an informational message instead of an empty grid.
  const isBridge = (syncStatus === 'syncing' || syncStatus === 'failed') && products.length === 0;

  // Genuinely empty (loaded, nothing there, not syncing) — hide entirely. While it's
  // still loading we keep the frame and show a "fetching…" placeholder (below).
  if (!isBridge && products.length === 0 && !loading) return null;

  // Key by absolute position, not ASIN alone: catalogues can list the same ASIN under
  // multiple SKUs, and duplicate React keys corrupt reconciliation across page changes
  // (rows losing cards / stale cards left behind) — same fix as UpdateTitlePage.
  const renderCard = (product, absoluteIdx) => {
    const isSelected = selectedAsins.includes(product.asin);
    return (
      <div key={`${product.asin}-${absoluteIdx}`} className={styles.productCardWrapperSingle}>
        <div
          className={`${styles.productCard} ${isSelected ? styles.productCardSelected : ''}`}
          onClick={() => onAsinSelect(product.asin)}
        >
          <div className={styles.productCardInner}>
            <div className={styles.productImage}>
              {product.image_url && (
                <img
                  src={product.image_url}
                  alt={product.title || product.asin}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
            </div>
            <div className={styles.productInfo}>
              <div className={styles.productInfoInner}>
                <div className={styles.productName}>{product.title || product.asin}</div>
                <div className={styles.skuTag}>
                  <span>{showAsin ? `ASIN : ${product.asin || 'N/A'}` : `SKU ID : ${product.sku || 'N/A'}`}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`${styles.modal} ${styles.modalInline}`}>
      <header className={`${styles.header} ${styles.headerInline}`} style={{ paddingBottom: '8px', paddingTop: '4px' }}>
        <h2>Select from Product Catalogue</h2>
      </header>

      {isBridge ? (
        <div style={{ padding: '32px 24px', textAlign: 'center', color: '#737373', fontSize: 14, lineHeight: 1.5 }}>
          {syncStatus === 'syncing'
            ? "Your catalogue is still syncing from Amazon (usually 5–10 minutes). Paste an ASIN above to start now."
            : (syncError || "We couldn't fetch your catalogue. Reconnect your Amazon account and try again.")}
        </div>
      ) : (
        <>
          <div className={styles.contentSingleSelect} style={{ minHeight: GRID_MIN_HEIGHT }}>
            {products.length === 0 ? (
              // Loading — reserve the products' space so the panel doesn't jump.
              <div style={{ minHeight: GRID_MIN_HEIGHT, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: '#737373', fontSize: 14 }}>
                <span>Fetching your catalogue — give us a moment…</span>
              </div>
            ) : (
              <>
                {pageProducts.length === 0 && (
                  <div className={styles.noResults}>No products match "{searchTerm}"</div>
                )}
                <div className={styles.productsGrid}>
                  {Array.from({ length: Math.ceil(pageProducts.length / PER_ROW) }).map((_, rowIndex) => (
                    <div key={rowIndex} className={`${styles.productsRow} ${styles.productsRowInline}`}>
                      {pageProducts
                        .slice(rowIndex * PER_ROW, rowIndex * PER_ROW + PER_ROW)
                        .map((product, idx) => renderCard(product, startIdx + rowIndex * PER_ROW + idx))}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <footer className={styles.actions} style={{ marginBottom:0 }}>
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  type="button"
                  className={styles.pageButton}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <FaChevronLeft />
                </button>
                <div className={styles.pageIndicator}>
                  <span>Page</span>
                  <input
                    type="number"
                    min={1}
                    max={totalPages}
                    value={currentPage}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      if (!value) return;
                      setCurrentPage(Math.min(totalPages, Math.max(1, value)));
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                    className={styles.pageInput}
                    aria-label="Page number"
                  />
                  <span>of {totalPages}</span>
                </div>
                <button
                  type="button"
                  className={styles.pageButton}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <FaChevronRight />
                </button>
              </div>
            )}
            <div className={styles.displayToggle}>
              <button
                type="button"
                className={`${styles.toggleButton} ${!showAsin ? styles.toggleButtonActive : ''}`}
                onClick={() => setShowAsin(false)}
              >
                SKU ID
              </button>
              <button
                type="button"
                className={`${styles.toggleButton} ${showAsin ? styles.toggleButtonActive : ''}`}
                onClick={() => setShowAsin(true)}
              >
                ASIN
              </button>
            </div>
          </footer>
        </>
      )}
    </div>
  );
};

export default InlineProductCatalogue;
